/**
 * The annotation model: what a labelled beat is, and how it changes.
 *
 * Pure — no React, no DOM, no signal rendering — so the part that decides what
 * a label MEANS can be tested without a browser. The drawing and the keyboard
 * live elsewhere.
 *
 * A label here is six global boundaries per beat — P start and end, QRS start
 * and end, T start and end — each one position judged across all twelve leads
 * at once, plus the beat's class and how far it can be trusted.
 *
 * WHY THESE FIELDS
 *
 * The task is a training set that cannot be built twice: the same 125 records
 * will not be annotated again. So the rule is to capture what only a person
 * can decide, and nothing a machine can recompute. Boundaries, beat class,
 * whether a beat is usable and whether the annotator was sure — those are
 * judgements. Wave peaks, amplitudes, areas, durations, QTc and axes are all
 * arithmetic over the signal and these boundaries, and are deliberately absent.
 */

/**
 * The six boundaries, in the order the keyboard walks them.
 *
 * Six GLOBAL positions, each an ENVELOPE across all twelve leads — not a
 * measurement repeated per lead, and not a judgement made on any single lead:
 *
 *   pStart    the EARLIEST P onset  of any lead
 *   pEnd      the LATEST   P offset of any lead
 *   qrsStart  the EARLIEST QRS onset  of any lead
 *   qrsEnd    the LATEST   QRS offset of any lead
 *   tStart    the EARLIEST T onset  of any lead
 *   tEnd      the LATEST   T offset of any lead
 *
 * That asymmetry is the whole definition and it is easy to lose: a start is a
 * minimum over leads, an end is a maximum, so the marked wave is at least as
 * wide as it looks in any one lead and usually wider than in all of them. A
 * boundary placed from lead II alone is wrong whenever some other lead starts
 * sooner or finishes later, which is most of the time.
 *
 * It is also what IEC 60601-2-51 and the CSE reference mean by a global
 * boundary, and it is a twelfth of the cost of annotating each lead.
 *
 * Peaks are deliberately absent: they were not asked for, and a peak is the one
 * fiducial a machine can re-find from the signal once the boundaries are known.
 *
 * `tStart` is the least reliable of the six — the ST segment slides into the T
 * wave with no clean transition, and the CSE reference omits it entirely. It is
 * captured because a second pass is not possible, and the `uncertain` flag
 * exists so the file can say when it was a guess.
 */
export const POINT_KEYS = [
    "pStart",
    "pEnd",
    "qrsStart",
    "qrsEnd",
    "tStart",
    "tEnd",
] as const;

export type PointKey = typeof POINT_KEYS[number];

/**
 * AAMI EC57 beat classes.
 *
 * `F` (fusion) is included because adding a class later means re-reviewing every
 * beat already labelled N or V, so the cost of leaving it out is a second pass —
 * exactly what this dataset cannot afford.
 */
export type BeatClass = "N" | "S" | "V" | "F" | "Q";

export const BEAT_CLASSES: BeatClass[] = ["N", "S", "V", "F", "Q"];

/** How a beat's marks came to be what they are. */
export type BeatStatus = "auto" | "confirmed" | "corrected";

export type Beat = {
    /** 1-based, in time order, including beats marked unusable. */
    number: number
    /**
     * The detected beat position, as supplied — not one of the six marks and not
     * something the technician places. It anchors the beat and identifies it.
     */
    rPeakMs: number
    points: {[key in PointKey]: number | null}
    beatClass: BeatClass
    /** False when the beat is too noisy to judge — a label, not an omission. */
    usable: boolean
    /** Leads that could not be judged for this beat, e.g. ["V3", "V4"]. */
    excludedLeads: string[]
    /** Points the annotator was not confident about. */
    uncertain: PointKey[]
    /**
     * The per-lead model's marks for this beat, keyed by canonical lead name
     * (`leadKeyOf`) — REFERENCE ONLY.
     *
     * Present when the record's input was `predictions_per_lead.csv`. They
     * seed the six global `points` (as their envelope) and are drawn on each
     * lead's row so the technician can see which lead sets each boundary. They
     * are never edited and never exported: the deliverable is always global
     * per beat.
     */
    reference?: {[lead: string]: LeadMarks}
    status: BeatStatus
    /**
     * When a human last touched THIS beat, ISO, or null if none has.
     *
     * Per beat rather than per file because the export used to stamp every row
     * with the technician's name and the moment the file was written — so a beat
     * nobody had looked at carried the same provenance as one they had worked
     * over, and the only thing telling them apart was `status`. For a training
     * set that is a quiet lie about where the labels came from.
     */
    reviewedAt: string | null
    comment: string
    /**
     * True when the server had no class for this beat. `beatClass` then reads
     * "N" for the screen, but a save writes null back — not a class nobody
     * chose. Cleared when the technician sets one.
     */
    classUnset?: boolean
    /** What the server had as the class when it was not one of ours, to write back as it was. */
    serverClass?: string | null
    /** True when the server had no R peak; `rPeakMs` is then only a placement. */
    rPeakUnset?: boolean
}

/**
 * One sample, in ms, at a record's own rate — never assumed. 2 ms at 500 Hz,
 * 4 at 250, 2.78 at 360.
 *
 * @param sampleRate Hz, finite and > 0
 */
export function sampleMs(sampleRate: number): number {
    return 1000 / sampleRate
}

/**
 * A time put on the record's sample grid, and inside the record.
 *
 * Every mark sits ON a sample — there is no signal between two — so every
 * move ends here. Positions stay in ms for the screen, but always an exact
 * multiple of one sample, so the cell's readout, the line and the sample the
 * server stores agree whatever the rate.
 *
 * @param ms
 * @param sampleRate
 * @param maxMs the record's last sample, when known
 */
export function snapToSample(ms: number, sampleRate: number, maxMs?: number): number {
    const step = sampleMs(sampleRate);
    const top = maxMs === undefined ? Infinity : Math.floor(maxMs / step + 1e-9) * step;

    return Math.min(top, Math.max(0, Math.round(ms / step) * step))
}

/**
 * The same judgement — marks, class, usable, unsure — whatever the status.
 *
 * What decides whether an edit changed anything: moving a mark and back, or
 * setting the class it already had, is not a correction.
 *
 * @param a
 * @param b
 */
export function sameJudgement(a: Beat, b: Beat): boolean {
    const sorted = (keys: PointKey[]) => keys.slice().sort().join(" ");

    return POINT_KEYS.every(key => a.points[key] === b.points[key])
        && a.beatClass === b.beatClass
        && Boolean(a.classUnset) === Boolean(b.classUnset)
        && a.usable === b.usable
        && sorted(a.uncertain) === sorted(b.uncertain)
}

/** When a beat was touched. Hoisted so every mutation stamps it the same way. */
function now(): string {
    return new Date().toISOString()
}

/** A blank beat, marked `auto` until a human has looked at it. */
export function emptyBeat(number: number, rPeakMs: number): Beat {
    const points = {} as {[key in PointKey]: number | null};

    POINT_KEYS.forEach(key => {
        points[key] = null
    });

    return {
        number: number,
        rPeakMs: rPeakMs,
        points: points,
        beatClass: "N",
        usable: true,
        excludedLeads: [],
        uncertain: [],
        status: "auto",
        reviewedAt: null,
        comment: "",
    }
}

/**
 * Move one point, and record that a human did it.
 *
 * Returns a new beat rather than mutating, so undo is a matter of keeping the
 * previous one.
 *
 * @param beat
 * @param key
 * @param deltaMs
 */
export function nudge(beat: Beat, key: PointKey, deltaMs: number): Beat {
    const current = beat.points[key];

    if (current === null) {
        return beat
    }

    const points = Object.assign({}, beat.points);

    points[key] = current + deltaMs;

    return Object.assign({}, beat, {points: points, status: "corrected" as BeatStatus, reviewedAt: now()})
}

/** Mark a point present at `ms`, or absent when `ms` is null. */
export function setPoint(beat: Beat, key: PointKey, ms: number | null): Beat {
    const points = Object.assign({}, beat.points);

    points[key] = ms;

    return Object.assign({}, beat, {points: points, status: "corrected" as BeatStatus, reviewedAt: now()})
}

/**
 * Make a point absent, and drop its "unsure" flag — there is nothing left to
 * be unsure about.
 *
 * @param beat
 * @param key
 */
export function clearPoint(beat: Beat, key: PointKey): Beat {
    if (beat.points[key] === null && beat.uncertain.indexOf(key) === -1) {
        return beat
    }

    return Object.assign(setPoint(beat, key, null), {uncertain: beat.uncertain.filter(k => k !== key)})
}

export function toggleUncertain(beat: Beat, key: PointKey): Beat {
    const has = beat.uncertain.indexOf(key) !== -1;

    return Object.assign({}, beat, {
        uncertain: has ? beat.uncertain.filter(k => k !== key) : beat.uncertain.concat(key),
        status: "corrected" as BeatStatus,
        reviewedAt: now(),
    })
}

export function setClass(beat: Beat, beatClass: BeatClass): Beat {
    if (beat.beatClass === beatClass && !beat.classUnset) {
        return beat
    }

    if (beat.classUnset) {
        return Object.assign({}, beat, {
            beatClass: beatClass, classUnset: false, status: "corrected" as BeatStatus, reviewedAt: now(),
        })
    }

    return Object.assign({}, beat, {beatClass: beatClass, status: "corrected" as BeatStatus, reviewedAt: now()})
}

export function toggleUsable(beat: Beat): Beat {
    return Object.assign({}, beat, {usable: !beat.usable, status: "corrected" as BeatStatus, reviewedAt: now()})
}

/** Accept the marks as they stand. Distinct from never having looked. */
export function confirm(beat: Beat): Beat {
    return beat.status === "auto"
        ? Object.assign({}, beat, {status: "confirmed" as BeatStatus, reviewedAt: now()})
        : beat
}

/**
 * Is a P wave present on this beat?
 *
 * Absence is a label, not a gap: atrial fibrillation and many ectopics have no
 * P at all, and a blank cell would let a model learn "missing means don't
 * care" instead of "there was none".
 *
 * @param beat
 */
export function hasP(beat: Beat): boolean {
    return beat.points.pStart !== null && beat.points.pEnd !== null
}

/**
 * Which way each boundary folds across leads.
 *
 * A start is a minimum, an end is a maximum. Written down as data rather than
 * left in prose because it is the rule the whole dataset depends on, and
 * because "earliest" and "latest" are the two words easiest to transpose.
 */
export const ENVELOPE_SIDE: {[key in PointKey]: "earliest" | "latest"} = {
    pStart: "earliest",
    pEnd: "latest",
    qrsStart: "earliest",
    qrsEnd: "latest",
    tStart: "earliest",
    tEnd: "latest",
};

export type LeadBoundaries = {[key in PointKey]: number | null};

/**
 * Fold per-lead boundaries into the six global ones.
 *
 * The envelope: earliest onset, latest offset, over whichever leads have a
 * value. A lead with no P contributes nothing rather than contributing a zero,
 * and a wave no lead has at all comes back null — absence stays a value.
 *
 * This is how a record that arrives with only `predictions_per_lead.csv` gets
 * its six global marks — see `beatFromReference`.
 *
 * @param perLead
 */
export function envelopeAcrossLeads(perLead: LeadBoundaries[]): LeadBoundaries {
    const out = {} as LeadBoundaries;

    POINT_KEYS.forEach(key => {
        const values = perLead
            .map(lead => lead[key])
            .filter((ms): ms is number => ms !== null);

        if (!values.length) {
            out[key] = null;

            return
        }

        out[key] = ENVELOPE_SIDE[key] === "earliest"
            ? Math.min.apply(null, values)
            : Math.max.apply(null, values)
    });

    return out
}

export type Cursor = {beat: number, key: PointKey};

/**
 * Walk one step through the boundaries, rolling into the next beat at the end.
 *
 * Pure, and separate from the hook, because the bug it encodes was invisible
 * from the outside: held as two pieces of React state and read from a closure,
 * two Tab presses landing in the same render both started from the same place
 * and the second overwrote the first. Pressing Tab quickly — which is how the
 * keyboard is actually used — silently skipped boundaries, and nothing failed.
 *
 * @param cursor where the selection is now
 * @param step +1 forward, -1 back
 * @param beatCount
 */
export function stepCursor(cursor: Cursor, step: number, beatCount: number): Cursor {
    if (beatCount <= 0) {
        return cursor
    }

    const at = (cursor.beat - 1) * POINT_KEYS.length + POINT_KEYS.indexOf(cursor.key) + step;
    const max = beatCount * POINT_KEYS.length;
    const clamped = Math.min(Math.max(0, at), max - 1);

    return {
        beat: Math.floor(clamped / POINT_KEYS.length) + 1,
        key: POINT_KEYS[clamped % POINT_KEYS.length],
    }
}

/** Counts for the progress readout, and for knowing when a record is done. */
export function progress(beats: Beat[]) {
    return {
        total: beats.length,
        reviewed: beats.filter(b => b.status !== "auto").length,
        corrected: beats.filter(b => b.status === "corrected").length,
        unusable: beats.filter(b => !b.usable).length,
        uncertain: beats.filter(b => b.uncertain.length > 0).length,
        withoutP: beats.filter(b => !hasP(b)).length,
    }
}

/* ------------------------------------------------------------------------ *
 * Per-lead reference
 * ------------------------------------------------------------------------ */

/**
 * A wave's start, peak and end on one lead, as the per-lead model gives them.
 * Reference only — see `Beat.reference`.
 */
export const LEAD_POINT_KEYS = [
    "pStart", "pPeak", "pEnd",
    "qrsStart", "qrsPeak", "qrsEnd",
    "tStart", "tPeak", "tEnd",
] as const;

export type LeadPointKey = typeof LEAD_POINT_KEYS[number];

export type LeadMarks = {[key in LeadPointKey]: number | null};

/** Diagnostic order, canonical (upper-case) spelling. */
export const LEAD_ORDER = ["I", "II", "III", "AVR", "AVL", "AVF", "V1", "V2", "V3", "V4", "V5", "V6"];

/**
 * One spelling per lead, whatever the source wrote.
 *
 * The signals say `AVR`, the per-lead file says `aVR`; keyed as written, a
 * lead's marks would never find its trace.
 *
 * @param lead
 */
export function leadKeyOf(lead: string): string {
    return lead.trim().toUpperCase()
}

/** The way a reader writes it: `aVR`, not `AVR`. */
export function leadLabel(lead: string): string {
    const upper = leadKeyOf(lead);

    return upper === "AVR" ? "aVR" : upper === "AVL" ? "aVL" : upper === "AVF" ? "aVF" : upper
}

/** Canonical lead names in diagnostic order, unknown leads after. */
export function orderLeads(leads: string[]): string[] {
    const keys = leads.map(leadKeyOf);
    const known = LEAD_ORDER.filter(lead => keys.indexOf(lead) !== -1);
    const rest = keys.filter(lead => LEAD_ORDER.indexOf(lead) === -1);

    return known.concat(rest.filter((lead, i) => rest.indexOf(lead) === i))
}

export function emptyLeadMarks(): LeadMarks {
    const marks = {} as LeadMarks;

    LEAD_POINT_KEYS.forEach(key => {
        marks[key] = null
    });

    return marks
}

/**
 * A beat seeded from per-lead marks.
 *
 * The six global points are the per-lead ENVELOPE — earliest onset, latest
 * offset over the leads — which is exactly what a global boundary means
 * (`ENVELOPE_SIDE`), so the technician starts from the right definition
 * rather than from any one lead. The per-lead marks ride along as reference.
 * Unreviewed: these are still the model's marks.
 *
 * @param number
 * @param rPeakMs
 * @param reference per-lead marks, keyed by canonical lead name
 */
export function beatFromReference(number: number, rPeakMs: number, reference: {[lead: string]: LeadMarks}): Beat {
    const points = envelopeAcrossLeads(Object.keys(reference).map(lead => {
        const boundaries = {} as LeadBoundaries;

        POINT_KEYS.forEach(key => {
            boundaries[key] = reference[lead][key]
        });

        return boundaries
    }));

    return Object.assign(emptyBeat(number, rPeakMs), {points: points, reference: reference})
}

/**
 * Where an ABSENT boundary appears when the technician adds it by keyboard or
 * by the ◀ ▶ buttons — a first position to nudge from, never a measurement.
 *
 * 1. The per-lead reference, when any lead has that wave on this beat: its
 *    envelope, earliest onset for a start and latest offset for an end — the
 *    same rule that defines the global mark.
 * 2. Otherwise a typical position relative to the beat's QRS (its marks when
 *    present, else the anchor ± 50 ms): P 160 to 40 ms before the QRS, T 80
 *    to 300 ms after it.
 *
 * Snapped to a sample, as every mark is, and kept inside the record.
 *
 * @param beat
 * @param key the absent boundary
 * @param sampleRate
 * @param maxMs the record's last sample, when known
 */
export function startingPoint(beat: Beat, key: PointKey, sampleRate: number, maxMs?: number): number {
    const snap = (ms: number) => snapToSample(ms, sampleRate, maxMs);

    if (beat.reference) {
        const values = Object.keys(beat.reference)
            .map(lead => beat.reference![lead][key])
            .filter((ms): ms is number => ms !== null);

        if (values.length) {
            return snap(ENVELOPE_SIDE[key] === "earliest"
                ? Math.min.apply(null, values)
                : Math.max.apply(null, values))
        }
    }

    const qrsStart = beat.points.qrsStart !== null ? beat.points.qrsStart : beat.rPeakMs - 50;
    const qrsEnd = beat.points.qrsEnd !== null ? beat.points.qrsEnd : beat.rPeakMs + 50;
    const typical: {[k in PointKey]: number} = {
        pStart: qrsStart - 160,
        pEnd: qrsStart - 40,
        qrsStart: qrsStart,
        qrsEnd: qrsEnd,
        tStart: qrsEnd + 80,
        tEnd: qrsEnd + 300,
    };

    return snap(Math.max(0, typical[key]))
}

/**
 * A boundary that breaks the order a beat's marks must follow.
 *
 *   order    `key` must come before `other` (strictly, inside one wave; at or
 *            before, where two waves meet)
 *   partial  the wave has `key` but not `other` — one end only
 *   next     `key` (this beat's last mark) runs past the next beat's
 *            `other` (its first mark)
 *   previous `key` (this beat's first mark) starts before the previous
 *            beat's `other` (its last mark)
 *
 * Every problem names both marks, so the screen can flag each cell involved.
 */
export type OrderProblem = {
    kind: "order" | "partial" | "next" | "previous"
    key: PointKey
    other: PointKey
}

const WAVES: {start: PointKey, end: PointKey}[] = [
    {start: "pStart", end: "pEnd"},
    {start: "qrsStart", end: "qrsEnd"},
    {start: "tStart", end: "tEnd"},
];

/** The last present mark of a beat, or null. */
function lastPresent(beat: Beat): PointKey | null {
    const found = POINT_KEYS.filter(key => beat.points[key] !== null);

    return found.length ? found[found.length - 1] : null
}

/** The first present of these keys on a beat, or null. */
function firstPresent(beat: Beat, keys: PointKey[]): PointKey | null {
    const found = keys.filter(key => beat.points[key] !== null);

    return found.length ? found[0] : null
}

/**
 * Everything wrong with the ORDER of a beat's marks.
 *
 * Within the beat, for the marks that are present:
 *
 *     pStart < pEnd ≤ qrsStart < qrsEnd ≤ tStart < tEnd
 *
 * strict inside a wave (a wave has a width), equal allowed where two waves
 * meet (one sample can end one and start the next). Each present mark is
 * compared with the nearest present mark before it, so an absent P does not
 * hide a T start that sits before the QRS.
 *
 * A wave must have both ends or neither: one end alone is not a wave, and
 * the server would store it as one.
 *
 * Across beats: this beat's last present mark must not run past the next
 * beat's first, and its first must not start before the previous beat's last
 * — whichever marks are present.
 *
 * Nothing here blocks an edit — moves stay free so the order can be fixed.
 * The screen flags the problems, and confirming or approving waits for them.
 *
 * @param beat
 * @param next the following beat, if any
 * @param previous the preceding beat, if any
 */
export function orderProblems(beat: Beat, next?: Beat | null, previous?: Beat | null): OrderProblem[] {
    const problems: OrderProblem[] = [];
    const at = (b: Beat, key: PointKey) => b.points[key];

    WAVES.forEach(({start, end}) => {
        const hasStart = at(beat, start) !== null;
        const hasEnd = at(beat, end) !== null;

        if (hasStart !== hasEnd) {
            problems.push({kind: "partial", key: hasStart ? start : end, other: hasStart ? end : start})
        }
    });

    let before: PointKey | null = null;

    POINT_KEYS.forEach(key => {
        const ms = at(beat, key);

        if (ms === null) {
            return
        }

        if (before !== null) {
            const prev = at(beat, before) as number;
            const sameWave = WAVES.some(w => w.start === before && w.end === key);

            if (sameWave ? !(prev < ms) : !(prev <= ms)) {
                problems.push({kind: "order", key: before, other: key})
            }
        }

        before = key
    });

    /* across beats: this beat's last mark against the next one's first, and
       the previous one's last against this one's first — whichever marks are
       present, so a missing T does not hide a QRS end inside the next beat */
    const mineFirst = firstPresent(beat, POINT_KEYS.slice());
    const mineLast = lastPresent(beat);

    if (next && mineLast !== null) {
        const theirs = firstPresent(next, POINT_KEYS.slice());

        if (theirs !== null && (at(beat, mineLast) as number) > (at(next, theirs) as number)) {
            problems.push({kind: "next", key: mineLast, other: theirs})
        }
    }

    if (previous && mineFirst !== null) {
        const theirs = lastPresent(previous);

        if (theirs !== null && (at(beat, mineFirst) as number) < (at(previous, theirs) as number)) {
            problems.push({kind: "previous", key: mineFirst, other: theirs})
        }
    }

    return problems
}
